import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type BundleRailwayTokenCheck } from "@/lib/api";
import { relativeIso } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const STATUS: Record<BundleRailwayTokenCheck["status"], { label: string; variant: React.ComponentProps<typeof Badge>["variant"] }> = {
  ok: { label: "Working", variant: "default" },
  missing: { label: "Missing", variant: "destructive" },
  failing: { label: "Failing", variant: "destructive" },
  wrong_project: { label: "Wrong project", variant: "destructive" },
  unsupported: { label: "Boo too old to check", variant: "outline" },
  unreachable: { label: "Boo unreachable", variant: "secondary" },
};

function detail(check: BundleRailwayTokenCheck): string | null {
  switch (check.status) {
    case "failing":
    case "unreachable":
      return check.reason;
    case "wrong_project":
      return `The Boo's token reaches project ${check.projectId}`;
    default:
      return null;
  }
}

export function BundleRailwayTokenSection({ containerId }: { containerId: string }) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryKey = ["admin-bundle-railway-token", containerId];

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => api.admin.bundleRailwayToken.check(containerId).then((r) => r.data),
  });

  const rotate = useMutation({
    mutationFn: () => api.admin.bundleRailwayToken.rotate(containerId).then((r) => r.data),
    onSuccess: (result) => {
      setConfirmOpen(false);
      if (result.check) queryClient.setQueryData(queryKey, result.check);
      else queryClient.invalidateQueries({ queryKey });
      if (result.revokeFailed.length > 0) {
        toast.warning(`New token in place; could not delete ${result.revokeFailed.join(", ")}. Delete them in Railway or rotate again.`, {
          duration: 15_000,
        });
      } else {
        toast.success(`New token in place; deleted ${result.revoked.length} old token(s)`);
      }
    },
    onError: (err) => {
      setConfirmOpen(false);
      toast.error(err instanceof Error ? err.message : "Rotation failed");
      queryClient.invalidateQueries({ queryKey });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2 py-1.5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-64" />
      </div>
    );
  }

  const status = data ? STATUS[data.brain.status] : null;
  const why = data ? detail(data.brain) : null;

  return (
    <div className="space-y-2 py-1">
      <div className="flex flex-wrap items-center gap-2">
        {status ? <Badge variant={status.variant}>{status.label}</Badge> : null}
        {isError ? (
          <span className="text-sm text-destructive">{error instanceof Error ? error.message : "Check failed"}</span>
        ) : null}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
            <RefreshCw className={`size-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
            Check
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={rotate.isPending || !data?.projectId}
            onClick={() => setConfirmOpen(true)}
            title={!data ? "Rotate needs a successful check first" : data.projectId ? undefined : "This Boo has no bundles project yet"}
          >
            <KeyRound className="size-3.5 mr-1.5" />
            Rotate
          </Button>
        </div>
      </div>
      {why ? <p className="text-xs text-muted-foreground break-all">{why}</p> : null}
      {data ? (
        <div className="text-xs text-muted-foreground">
          {data.projectId && data.tokens === null ? (
            <>
              Project <span className="font-mono">{data.projectId}</span>: Railway's token list could not be read
              {data.tokensError ? ` (${data.tokensError})` : ""}.
            </>
          ) : data.projectId && data.tokens ? (
            <>
              Project <span className="font-mono">{data.projectId}</span> has {data.tokens.length} token
              {data.tokens.length === 1 ? "" : "s"}
              {data.tokens.length > 0 ? ":" : "."}
              <ul className="mt-1 space-y-0.5">
                {data.tokens.map((t) => (
                  <li key={`${t.name}-${t.createdAt}`} className="font-mono">
                    {t.name} · created {relativeIso(t.createdAt)}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            "No bundles project yet."
          )}
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Rotate the bundles Railway token?"
        description="Mints a new token, hands it to this Boo, then deletes every other token on its bundles project. Anything else using an old token stops working, including a repository whose CI deploys with a copy of it."
        confirmLabel="Rotate"
        variant="destructive"
        loading={rotate.isPending}
        onConfirm={() => rotate.mutate()}
      />
    </div>
  );
}
